import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Notification } from '@/types/dashboard';
import { getStoredUserIdFromLocalStorage } from '@/lib/storedAuthUser';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationHeader } from './NotificationHeader';
import { NotificationFilters } from './NotificationFilters';
import { NotificationList } from './NotificationList';

export function NotificationsContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const searchQuery = searchParams.get('q') ?? '';
  const typeFilter = searchParams.get('type') ?? 'all';
  const readFilter = searchParams.get('read') ?? 'all';
  const sortOrder = searchParams.get('ordering') ?? '-timestamp';
  const pageParam = searchParams.get('page');
  const page = pageParam ? parseInt(pageParam, 10) : undefined;

  const readFilterValue = useMemo(() => {
    if (readFilter === 'all') return null;
    if (readFilter === 'read') return true;
    return false;
  }, [readFilter]);

  const filters = useMemo(
    () => ({
      search: searchQuery || undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined,
      read: readFilterValue,
      ordering: sortOrder !== '-timestamp' ? sortOrder : undefined,
      page: page && page > 0 ? page : undefined,
    }),
    [searchQuery, typeFilter, readFilterValue, sortOrder, page]
  );

  const {
    notifications,
    loading,
    error,
    pagination,
    refetch,
    markAsRead,
    markAllAsRead,
    markAsUnread,
    deleteNotification,
  } = useNotifications(filters);

  const updateFilters = useCallback(
    (updates: Record<string, string>) => {
      const params = new URLSearchParams(searchParams.toString());
      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== 'all' && value !== '-timestamp') {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
      navigate(`/notifications?${params.toString()}`);
    },
    [navigate, searchParams]
  );

  const handleSearchChange = useCallback((value: string) => updateFilters({ q: value }), [updateFilters]);
  const handleTypeFilterChange = useCallback((value: string) => updateFilters({ type: value }), [updateFilters]);
  const handleReadFilterChange = useCallback((value: string) => updateFilters({ read: value }), [updateFilters]);
  const handleSortChange = useCallback((value: string) => updateFilters({ ordering: value }), [updateFilters]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsReadHandler = async (notification: Notification) => {
    await markAsRead(notification);
  };

  const markAsUnreadHandler = async (notification: Notification) => {
    await markAsUnread(notification);
  };

  const markAllAsReadHandler = async () => {
    const userId =
      typeof window !== 'undefined' ? getStoredUserIdFromLocalStorage() : undefined;
    await markAllAsRead(userId);
  };

  const deleteNotificationHandler = async (notification: Notification) => {
    await deleteNotification(notification);
  };

  const handlePageChange = useCallback(
    (pageNum: number) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', String(pageNum));
      navigate(`/notifications?${params.toString()}`);
    },
    [navigate, searchParams]
  );

  return (
    <div className="space-y-6">
      <NotificationHeader
        unreadCount={unreadCount}
        loading={loading}
        onRefresh={refetch}
        onMarkAllAsRead={markAllAsReadHandler}
      />
      <NotificationFilters
        searchQuery={searchQuery}
        typeFilter={typeFilter}
        readFilter={readFilter}
        sortOrder={sortOrder}
        onSearchChange={handleSearchChange}
        onTypeFilterChange={handleTypeFilterChange}
        onReadFilterChange={handleReadFilterChange}
        onSortChange={handleSortChange}
      />
      <NotificationList
        notifications={notifications}
        loading={loading}
        error={error}
        pagination={pagination}
        onRefetch={refetch}
        onMarkAsRead={markAsReadHandler}
        onMarkAsUnread={markAsUnreadHandler}
        onDelete={deleteNotificationHandler}
        onPageChange={pagination?.next || pagination?.previous ? handlePageChange : undefined}
      />
    </div>
  );
}
